import { useMemo } from "react";
import { ConnectProto } from "@racoon/api";
import { skipToken } from "@reduxjs/toolkit/query";
import { useListCompatibleContainerGroupsForTruckQuery } from "~/shared/api/container-group/container-group.grpc";
import { useGetTruckTypeByIdQuery } from "~/shared/api/truck/truck-type.api";

interface UseCompatibleContainerGroupsArgs {
  ownerOrganizationId?: bigint;
  /** The route's division; the backend adds its zone's groups. Defaults to the owner. */
  routeOwnerId?: bigint;
  /** Container groups are narrowed to what this truck can service. */
  truckId?: bigint;
  /** Its custom truck type, whose capability matrix narrows the job types. */
  truckTypeId?: bigint;
  /** A modal that stays mounted while closed passes its open state, so it fetches nothing. */
  enabled?: boolean;
}

/**
 * The container groups a truck can service, plus its capability matrix.
 *
 * The backend list is per container group: it includes groups the truck can
 * handle at least ONE job type of, so the matrix is still needed to narrow the
 * job types offered for each of them.
 */
export function useCompatibleContainerGroups({
  ownerOrganizationId,
  routeOwnerId = ownerOrganizationId,
  truckId,
  truckTypeId,
  enabled = true,
}: UseCompatibleContainerGroupsArgs) {
  const hasTruck = truckId != null && truckId !== 0n;

  // `currentData`, not `data`: while a new truck's request is in flight, `data`
  // still holds the previous truck's list, and a picker built on it would offer
  // (and submit) groups the selected truck cannot service.
  const { currentData: compatibleResponse, isFetching: isLoadingContainerGroups } =
    useListCompatibleContainerGroupsForTruckQuery(
      enabled && hasTruck && routeOwnerId
        ? { ownerOrganizationId: routeOwnerId, truckId: truckId! }
        : skipToken,
    );

  const { currentData: truckType, isFetching: isLoadingTruckType } = useGetTruckTypeByIdQuery(
    enabled && truckTypeId && ownerOrganizationId
      ? { id: truckTypeId, ownerOrganizationId }
      : skipToken,
  );

  // "containerTypeId|jobType" pairs the truck can handle.
  const truckCapabilities = useMemo(() => {
    const set = new Set<string>();
    (truckType?.truckTypeCapabilities ?? []).forEach((cap) => {
      if (cap.containerType?.id && cap.jobType) {
        set.add(`${cap.containerType.id}|JOB_TYPE_${ConnectProto.JobType[cap.jobType]}`);
      }
    });
    return set;
  }, [truckType]);

  // Memoised: a fresh empty array every render would invalidate whatever the
  // caller derives from it.
  const containerGroups = useMemo(
    () => compatibleResponse?.containerGroups ?? [],
    [compatibleResponse],
  );

  return {
    containerGroups,
    truckCapabilities,
    /** Without a custom truck type there is no matrix to narrow by. */
    hasCapabilityMatrix: enabled && truckTypeId != null,
    /** Until the truck type arrives the empty set means "unknown", not "can do nothing". */
    areCapabilitiesReady: !enabled || truckTypeId == null || (!isLoadingTruckType && !!truckType),
    isLoading: isLoadingContainerGroups || isLoadingTruckType,
  };
}
